import { nodesFlux } from '@candulabs/craft';
import { css, ModeProvider, styled } from 'canoe';
import React, { useMemo, useState } from 'react';

import { BuiltInComponents, CRAFT_ROOT_ID, isTooltipDocument } from 'craft/constants';
import { FlowsFloatingMenu } from 'craft/containers/FlowsFloatingMenu';
import { TooltipFloatingMenu } from 'craft/containers/TooltipFloatingMenu';
import { useEditorSelector, useSelection } from 'craft/hooks';
import { useCurrentSnapshot, useFlowAncestor, useToolboxState } from 'craft/state';

const FixedWrapper = styled.div<{ menuPosition: 'top' | 'bottom'; isToolboxDocked: boolean }>`
  ${({ menuPosition, isToolboxDocked }) => css`
    width: max-content;
    display: flex;
    position: fixed;

    ${menuPosition === 'top'
      ? css`
          top: 140px;
        `
      : css`
          bottom: 30px;
        `}

    left: 50%;
    z-index: 11;

    transform: translate(
      calc(-50% + 31px - ${isToolboxDocked ? '0px' : '150px'}),
      -50%
    ); // enter in viewport by adding half panel width and subtracting half toolbox width
  `}
`;

const Container = styled.div`
  ${({ theme }) => css`
    height: 64px;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 10px;
    flex-shrink: 0;

    padding: 0 ${theme.spaces.s} 0 ${theme.spaces.normal};
    border-radius: ${theme.borderRadius.small};
    background-color: ${theme.colors.OnyxGrayD10};

    position: relative;
  `}
`;

export const FloatingMenu = React.memo(() => {
  const [menuPosition, setMenuPosition] = useState<'top' | 'bottom'>('bottom');
  const name = useEditorSelector(nodesFlux.selectors.getNodeName(CRAFT_ROOT_ID));
  const { selection } = useSelection();
  const { flowNodeId } = useFlowAncestor(selection.first);
  const nodeType = useEditorSelector(nodesFlux.selectors.getNodeName(selection.first));
  const { snapshot, isSnapshotLoaded } = useCurrentSnapshot();
  const { isToolboxDocked } = useToolboxState();

  const FloatingMenuComponent = useMemo(() => {
    if (flowNodeId || nodeType === BuiltInComponents.Flow) {
      return (
        <FlowsFloatingMenu
          flowId={flowNodeId || selection.first}
          onToggleMenuPosition={() => setMenuPosition(menuPosition === 'top' ? 'bottom' : 'top')}
          menuPosition={menuPosition}
        />
      );
    }

    if (isTooltipDocument(name)) {
      return (
        <TooltipFloatingMenu
          contentType={name}
          onToggleMenuPosition={() => setMenuPosition(menuPosition === 'top' ? 'bottom' : 'top')}
          menuPosition={menuPosition}
        />
      );
    }

    return <></>;
  }, [flowNodeId, nodeType, selection.first, name, menuPosition, isTooltipDocument]);

  if (snapshot && !isSnapshotLoaded) {
    // if snapshot is loading, hide floating menu
    return null;
  }

  if (!flowNodeId && nodeType !== BuiltInComponents.Flow && !isTooltipDocument(name)) {
    // if not in flow and not a tooltip document, hide floating menu
    return null;
  }

  return (
    <ModeProvider value={{ mode: 'Dark' }}>
      <FixedWrapper menuPosition={menuPosition} isToolboxDocked={isToolboxDocked}>
        <Container id="floating-menu" data-craft-ignore-events>
          {FloatingMenuComponent}
        </Container>
      </FixedWrapper>
    </ModeProvider>
  );
});

FloatingMenu.displayName = 'FloatingMenu';
